import {
  SkeletonPage,
  SkeletonTabs,
  SkeletonDisplayText,
  SkeletonBodyText,
  Layout,
  Card,
  BlockStack,
} from "@shopify/polaris";
import DataTableSkeleton from "components/datatable/DataTableSkeleton";

/**
 * Loading placeholder for the customer detail page. Mirrors the real layout —
 * title + primary action, the tab bar, the main content, and the right-hand
 * sidebar cards — so the page doesn't jump once the customer loads. Used on
 * first load (and while switching to a different customer) in place of a bare
 * full-page spinner.
 *
 * The main area is tab-aware: each tab renders a different table, so the
 * skeleton matches the table (column count + action-icon columns) of whichever
 * tab is being loaded. Keyed by the customer-container tab's `key` (from
 * TABS_CONFIG), not its position, so the tabs can be reordered freely.
 */
const TAB_TABLE_CONFIG = {
  orders: { columns: 8, iconColumnIndexes: [7] },
  products: { columns: 7, iconColumnIndexes: [] },
  invoices: { columns: 9, iconColumnIndexes: [8] },
  salesreturns: { columns: 8, iconColumnIndexes: [] },
  emails: { columns: 5, iconColumnIndexes: [4] },
  // Expand chevron / Data / Terminas / Pavadinimas / Serija-Nr. / Eilučių / Būsena
  reservations: { columns: 7, iconColumnIndexes: [] },
};

const DOCUMENTS_TAB_KEY = "documents";
const SETTLEMENTS_TAB_KEY = "settlements";

// Expander / Data / Type / Journal / Series-No. / Due / Amount / Amount EUR /
// Paid / Debt — the first column is the chevron that opens a document's lines.
const SETTLEMENTS_TABLE_COLUMNS = 10;

const SidebarCard = ({ lines }) => (
  <Card roundedAbove="sm">
    <BlockStack gap="300">
      <SkeletonDisplayText size="small" />
      <SkeletonBodyText lines={lines} />
    </BlockStack>
  </Card>
);

// The settlements tab opens on summary cards, not straight into a table —
// mirror that shape (six stat tiles) above the ledger table skeleton.
const SettlementsSkeleton = () => (
  <BlockStack gap="400">
    <div
      className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6"
      aria-hidden="true"
    >
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          data-testid="settlements-skeleton-tile"
          className="rounded-xl border border-[#E6E1DB] bg-white p-4"
        >
          <div className="h-3 w-20 rounded bg-gray-200 animate-pulse" />
          <div className="mt-2 h-5 w-24 rounded bg-gray-200 animate-pulse" />
        </div>
      ))}
    </div>
    <DataTableSkeleton
      rows={8}
      columns={SETTLEMENTS_TABLE_COLUMNS}
      iconColumnIndexes={[]}
    />
  </BlockStack>
);

// The documents tab is a file list (title + search + rows), not a table.
const DocumentsSkeleton = () => (
  <BlockStack gap="400">
    <SkeletonDisplayText size="small" />
    <div className="h-9 w-full rounded bg-gray-100 animate-pulse" aria-hidden="true" />
    <div className="animate-pulse flex flex-col" aria-hidden="true">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-3 py-3 border-b border-[#F2F1EF] last:border-b-0"
        >
          <div className="h-9 w-9 rounded bg-gray-200" />
          <div className="flex-grow">
            <div className="h-3 w-1/2 rounded bg-gray-200 mb-2" />
            <div className="h-3 w-1/4 rounded bg-gray-100" />
          </div>
          <div className="h-7 w-7 rounded bg-gray-200" />
          <div className="h-7 w-7 rounded bg-gray-200" />
        </div>
      ))}
    </div>
  </BlockStack>
);

export default function CustomerContainerSkeleton({
  selected = "orders",
  tabCount = 6,
}) {
  const tableConfig = TAB_TABLE_CONFIG[selected] || TAB_TABLE_CONFIG.orders;

  return (
    <SkeletonPage fullWidth primaryAction backAction>
      <SkeletonTabs count={tabCount} />
      <div style={{ marginTop: "16px" }}>
        <Layout>
          <Layout.Section>
            <Card roundedAbove="sm">
              {selected === DOCUMENTS_TAB_KEY ? (
                <DocumentsSkeleton />
              ) : selected === SETTLEMENTS_TAB_KEY ? (
                <SettlementsSkeleton />
              ) : (
                <DataTableSkeleton
                  rows={8}
                  columns={tableConfig.columns}
                  iconColumnIndexes={tableConfig.iconColumnIndexes}
                />
              )}
            </Card>
          </Layout.Section>
          <Layout.Section variant="oneThird">
            <BlockStack gap="500">
              {/* finance history */}
              <SidebarCard lines={3} />
              {/* customer details */}
              <SidebarCard lines={6} />
              {/* questionnaire */}
              <SidebarCard lines={2} />
              {/* contacts */}
              <SidebarCard lines={3} />
              {/* addresses */}
              <SidebarCard lines={2} />
              {/* finvalda */}
              <SidebarCard lines={3} />
            </BlockStack>
          </Layout.Section>
        </Layout>
      </div>
    </SkeletonPage>
  );
}
